import { useState, useMemo } from 'react';
import axios from 'axios';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { S, Icon } from '../regureStyles';

const BASE_URL = process.env.REACT_APP_BASE_URL || '';

const GenerateModal = ({ carriers, onClose, onGenerated }) => {
  const { t, i18n } = useTranslation();
  const history = useHistory();
  const now = new Date();
  const prevTo = new Date(now.getFullYear(), now.getMonth(), 0);
  const prevFrom = new Date(prevTo.getFullYear(), prevTo.getMonth(), 1);

  const [form, setForm] = useState({
    carrierId: carriers[0]?.id || '',
    type: 'claims',
    periodFrom: prevFrom.toISOString().slice(0, 10),
    periodTo: prevTo.toISOString().slice(0, 10),
  });
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(false);
  const [validation, setValidation] = useState(null); // { errors, warnings, errorCount, warningCount }
  const [postGenWarnings, setPostGenWarnings] = useState(null); // shown after a generation that succeeded with warnings
  const [warningsCollapsed, setWarningsCollapsed] = useState(false);

  const hasErrors   = !!(validation && validation.errorCount > 0);
  const hasWarnings = !!(validation && validation.warningCount > 0);
  const hasValidated = validation !== null;

  const allClean = hasValidated && !hasErrors && !hasWarnings;

  const runValidation = async () => {
    if (!form.carrierId) return null;
    setValidating(true);
    try {
      const resp = await axios.post(
        `${BASE_URL}/bordereaux/validate`,
        { carrierId: form.carrierId, periodFrom: form.periodFrom, periodTo: form.periodTo },
        { withCredentials: true },
      );
      const data = resp.data || {};
      const result = { errors: data.errors || [], warnings: data.warnings || [], errorCount: data.errorCount || 0, warningCount: data.warningCount || 0 };
      setValidation(result);
      setPostGenWarnings(null);
      return result;
    } catch (err) {
      setValidation({ errors: [{ description: err.response?.data?.message || err.message || t('screens.bordereaux.genValidationFailed'), severity: 'error' }], warnings: [], errorCount: 1, warningCount: 0 });
      return null;
    } finally {
      setValidating(false);
    }
  };

  const handleGenerate = async () => {
    if (!form.carrierId) return;
    setLoading(true);
    try {
      // Send the user's current UI locale so the generated Excel/CSV
      // renders column headers and title in that language, not English.
      const uiLocale = (i18n.language || 'en').slice(0, 2);
      const resp = await axios.post(
        `${BASE_URL}/bordereaux/generate`,
        {
          carrierId: form.carrierId,
          type: form.type,
          periodFrom: form.periodFrom,
          periodTo: form.periodTo,
          locale: uiLocale,
        },
        { withCredentials: true },
      );
      // Server returns 202 with possible warnings
      if (resp.data?.warningCount > 0) {
        setPostGenWarnings({ warnings: resp.data.warnings || [], warningCount: resp.data.warningCount });
      }
      if (resp.data?.data) onGenerated();
      // Close immediately when fully clean; keep modal open if warnings to show.
      if (!resp.data?.warningCount) onClose();
    } catch (err) {
      // 422 — validation_failed
      if (err.response?.status === 422) {
        const data = err.response.data || {};
        setValidation({ errors: data.errors || [], warnings: data.warnings || [], errorCount: data.errorCount || 0, warningCount: data.warningCount || 0 });
        setPostGenWarnings(null);
      }
    } finally {
      setLoading(false);
    }
  };

  const sel = k => e => {
    setForm(f => ({ ...f, [k]: e.target.value }));
    // Any form change invalidates the previous validation result.
    setValidation(null);
    setPostGenWarnings(null);
  };

  const selectStyle = {
    width: '100%',
    padding: '9px 12px',
    background: S.bgCard,
    border: `1px solid ${S.borderLight}`,
    borderRadius: 8,
    fontSize: 13,
    color: S.textPrimary,
    outline: 'none',
  };
  const inputStyle = { ...selectStyle, boxSizing: 'border-box' };

  // Generate button state — driven by validation results.
  const canGenerate = !loading && form.carrierId && !hasErrors;
  const generateLabel = loading ? t('screens.bordereaux.genSubmitting') : (hasWarnings ? t('screens.bordereaux.genGenerateAnyway') : t('screens.bordereaux.genGenerate'));
  const generateBg = hasWarnings
    ? 'linear-gradient(135deg, #f59e0b, #d97706)'
    : `linear-gradient(135deg, ${S.burgundy}, ${S.burgundy2})`;

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div style={{ background: '#fff', borderRadius: 20, padding: 28, width: 560, maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: S.textPrimary }}>{t('screens.bordereaux.genTitle')}</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
            <Icon name='x' color={S.textMuted} size={18} />
          </button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div>
            <div style={{ fontSize: 11, fontWeight: 600, color: S.textMuted, textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 5 }}>{t('screens.bordereaux.genRiskCarrier')}</div>
            <select value={form.carrierId} onChange={sel('carrierId')} style={selectStyle}>
              {carriers.map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>
          <div>
            <div style={{ fontSize: 11, fontWeight: 600, color: S.textMuted, textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 5 }}>{t('screens.bordereaux.genReportType')}</div>
            <select value={form.type} onChange={sel('type')} style={selectStyle}>
              <option value='claims'>{t('screens.bordereaux.genTypeClaims')}</option>
              <option value='premium'>{t('screens.bordereaux.genTypePremium')}</option>
              <option value='combined'>{t('screens.bordereaux.genTypeCombined')}</option>
              <option value='loss_run'>{t('screens.bordereaux.genTypeLossRun')}</option>
              <option value='commission'>{t('screens.bordereaux.genTypeCommission')}</option>
            </select>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <div style={{ fontSize: 11, fontWeight: 600, color: S.textMuted, textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 5 }}>{t('screens.bordereaux.genPeriodFrom')}</div>
              <input type='date' value={form.periodFrom} onChange={sel('periodFrom')} style={inputStyle} />
            </div>
            <div>
              <div style={{ fontSize: 11, fontWeight: 600, color: S.textMuted, textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 5 }}>{t('screens.bordereaux.genPeriodTo')}</div>
              <input type='date' value={form.periodTo} onChange={sel('periodTo')} style={inputStyle} />
            </div>
          </div>
        </div>

        {/* Validation results panel */}
        {validation && (
          <ValidationResultsPanel
            validation={validation}
            allClean={allClean}
            history={history}
            onClose={onClose}
            t={t}
          />
        )}

        {/* Post-generation warnings panel — shown if Generate succeeded with warnings */}
        {postGenWarnings && (
          <div style={{ marginTop: 16, border: `1px solid #fde68a`, background: '#fffbeb', borderRadius: 10, padding: 12 }}>
            <div
              onClick={() => setWarningsCollapsed(c => !c)}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', userSelect: 'none' }}
            >
              <div style={{ fontSize: 13, fontWeight: 700, color: '#92400e' }}>
                {t('screens.bordereaux.genPostWarnings', { count: postGenWarnings.warningCount })}
              </div>
              <span style={{ color: '#92400e', fontSize: 12 }}>{warningsCollapsed ? '▸' : '▾'}</span>
            </div>
            {!warningsCollapsed && (
              <div style={{ marginTop: 10, maxHeight: 220, overflowY: 'auto' }}>
                {postGenWarnings.warnings.map((w, i) => (
                  <div key={i} style={{ display: 'flex', gap: 8, padding: '6px 0', fontSize: 12, color: '#78350f', borderTop: i ? '1px solid #fde68a' : 'none' }}>
                    <span style={{ fontWeight: 600, minWidth: 110 }}>{w.policyNumber || '—'}</span>
                    <span>{w.description}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 20 }}>
          <button
            onClick={onClose}
            style={{
              padding: '9px 18px',
              background: S.bgCard,
              border: `1px solid ${S.borderLight}`,
              borderRadius: 10,
              fontSize: 13,
              fontWeight: 500,
              color: S.textSecondary,
              cursor: 'pointer',
            }}
          >
            {t('screens.bordereaux.genCancel')}
          </button>

          <button
            onClick={runValidation}
            disabled={validating || !form.carrierId}
            style={{
              padding: '9px 18px',
              background: '#fff',
              color: S.burgundy,
              border: `1px solid ${S.burgundy}`,
              borderRadius: 10,
              fontSize: 13,
              fontWeight: 600,
              cursor: validating || !form.carrierId ? 'not-allowed' : 'pointer',
              opacity: validating || !form.carrierId ? 0.6 : 1,
            }}
          >
            {validating ? t('screens.bordereaux.genValidating') : t('screens.bordereaux.genValidate')}
          </button>

          <button
            onClick={handleGenerate}
            disabled={!canGenerate}
            title={hasErrors ? t('screens.bordereaux.genFixErrorsTitle') : ''}
            style={{
              padding: '9px 22px',
              background: generateBg,
              color: '#fff',
              border: 'none',
              borderRadius: 10,
              fontSize: 13,
              fontWeight: 600,
              cursor: canGenerate ? 'pointer' : 'not-allowed',
              opacity: canGenerate ? 1 : 0.45,
            }}
          >
            {generateLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

const ValidationResultsPanel = ({ validation, allClean, history, onClose, t }) => {
  const totalCount = validation.errorCount + validation.warningCount;

  if (allClean) {
    return (
      <div style={{ marginTop: 18, padding: 14, background: '#ecfdf5', border: '1px solid #a7f3d0', borderRadius: 10, display: 'flex', alignItems: 'center', gap: 10 }}>
        <span style={{ fontSize: 18 }}>✅</span>
        <div style={{ fontSize: 13, fontWeight: 600, color: '#065f46' }}>{t('screens.bordereaux.genAllClean')}</div>
      </div>
    );
  }

  return (
    <div style={{ marginTop: 18, border: '1px solid #e2e8f0', borderRadius: 10, overflow: 'hidden' }}>
      <div style={{ padding: '10px 14px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: '#1e293b' }}>{t('screens.bordereaux.genResultsTitle')}</div>
        <div style={{ display: 'flex', gap: 8 }}>
          {validation.errorCount > 0 && (
            <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 9px', borderRadius: 999, background: '#fef2f2', color: '#991b1b', border: '1px solid #fecaca' }}>
              {t('screens.bordereaux.genErrorCount', { count: validation.errorCount })}
            </span>
          )}
          {validation.warningCount > 0 && (
            <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 9px', borderRadius: 999, background: '#fffbeb', color: '#92400e', border: '1px solid #fde68a' }}>
              {t('screens.bordereaux.genWarningCount', { count: validation.warningCount })}
            </span>
          )}
        </div>
      </div>

      <div style={{ maxHeight: 260, overflowY: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr>
              <th style={thStyle}>{t('screens.bordereaux.genColPolicy')}</th>
              <th style={thStyle}>{t('screens.bordereaux.genColField')}</th>
              <th style={thStyle}>{t('screens.bordereaux.genColIssue')}</th>
              <th style={thStyle}>{t('screens.bordereaux.genColSeverity')}</th>
            </tr>
          </thead>
          <tbody>
            {[...validation.errors, ...validation.warnings].map((f, i) => (
              <tr key={i}>
                <td style={tdStyle}>
                  {f.policyNumber && f.policyNumber !== '(unknown)' ? (
                    <a
                      href='#'
                      onClick={e => { e.preventDefault(); onClose(); history.push(`/policies?search=${encodeURIComponent(f.policyNumber)}`); }}
                      style={{ color: '#800020', textDecoration: 'none', fontWeight: 600 }}
                    >
                      {f.policyNumber}
                    </a>
                  ) : '—'}
                </td>
                <td style={tdStyle}>{f.field || '—'}</td>
                <td style={tdStyle}>{f.description}</td>
                <td style={{ ...tdStyle, color: f.severity === 'error' ? '#991b1b' : '#92400e', fontWeight: 600, textTransform: 'capitalize' }}>
                  {f.severity}
                </td>
              </tr>
            ))}
            {totalCount === 0 && (
              <tr><td colSpan={4} style={{ ...tdStyle, textAlign: 'center', color: '#94a3b8' }}>{t('screens.bordereaux.genNoIssues')}</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const thStyle = { fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5, padding: '8px 12px', borderBottom: '1px solid #e2e8f0', background: '#f8fafc', textAlign: 'left' };
const tdStyle = { padding: '8px 12px', borderBottom: '1px solid #f1f5f9', color: '#1e293b', verticalAlign: 'top' };

export default GenerateModal;
